import { sub } from 'date-fns';
import React, { useCallback, useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { Col, Grid, Row } from 'react-styled-flexboxgrid';
import { useTheme } from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import {
  Datepicker,
  FormGroup,
  Input,
} from '@savgroup-front-common/core/atoms/Form';
import { Heading } from '@savgroup-front-common/core/atoms/Heading';
import SwipeableDrawer, {
  DRAWER_SIZE,
} from '@savgroup-front-common/core/atoms/SwipeableDrawer/SwipeableDrawer';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { FLEX_ALIGN } from '@savgroup-front-common/types';
import useSellerSelfcareConfiguration from '@/hooks/useSellerSelfcareConfiguration';
import { CreateClaimValues } from '@/types';
import StepLayout from '@/view/app/layout/NewDesignLayout/StepLayout/StepLayout';
import STEP_CONFIG from '@/view/app/Routes/StepConfig';

import messagesForm from './messages';
import usePurchaseDateLoginEnable from './PurchaseDateLoginEnable.hooks';
import {
  $HelperButton,
  $HelperContainerTitle,
  $Img,
} from './PurchaseDateLoginEnable.styles';

interface PurchaseDateLoginEnableProps {
  values: CreateClaimValues;
}

const PurchaseDateLoginEnable: React.FC<PurchaseDateLoginEnableProps> = ({
  values,
}) => {
  const [isHelperOpen, setIsOpenHelper] = useState<boolean>(false);
  const theme = useTheme();
  const { formContext, isLoading, onSubmit } = usePurchaseDateLoginEnable({
    values,
  });
  const { sellerConfiguration } = useSellerSelfcareConfiguration({
    sellerId: values.sellerId,
  });

  const {
    formState: { errors, isValid },
    control,
    handleSubmit,
  } = formContext;

  const handleHelper = useCallback(() => {
    setIsOpenHelper((prev) => !prev);
  }, []);

  return (
    <FormProvider {...formContext}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StepLayout
          values={values}
          stepName={STEP_CONFIG.CUSTOMER_INVOICE_INFORMATION}
          isDisabled={!isValid}
          isLoading={isLoading}
        >
          <Grid fluid>
            <Row>
              <Col sm={12} xs={12}>
                <FormGroup>
                  <Controller
                    control={control}
                    name="orderReference"
                    render={({ field }) => {
                      return (
                        <Input
                          name={field.name}
                          value={field.value}
                          label={messagesForm.orderReferenceLabel}
                          disableBrowserAutocomplete
                          onChange={field.onChange}
                          errors={errors}
                          postLabel={
                            sellerConfiguration?.orderHelpText &&
                            sellerConfiguration?.orderHelpImageUrls?.length >
                              0 ? (
                              <$HelperButton
                                hollow
                                naked
                                underline
                                type={BUTTON_TYPES.BUTTON}
                                onClick={handleHelper}
                              >
                                <SafeFormattedMessageWithoutSpread
                                  message={
                                    messagesForm.orderReferenceHelperLabel
                                  }
                                />
                              </$HelperButton>
                            ) : undefined
                          }
                          fullWidthLabel
                          isRequired
                        />
                      );
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col sm={12} xs={12}>
                <FormGroup>
                  <Controller
                    name="purchaseDate"
                    control={control}
                    render={({ field }) => (
                      <Datepicker
                        name={field.name}
                        errors={errors}
                        label={messagesForm.purchaseDateLabel}
                        minDate={sub(new Date(), { years: 100 })}
                        maxDate={new Date()}
                        onChange={field.onChange}
                        value={field.value}
                        isRequired
                      />
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Grid>
        </StepLayout>
      </form>
      {sellerConfiguration && (
        <SwipeableDrawer
          isOpen={isHelperOpen}
          size={DRAWER_SIZE.SMALL}
          onClose={() => setIsOpenHelper(false)}
          hasBackdrop
          hasBackdropScroll={false}
        >
          <$HelperContainerTitle>
            <Heading
              level={2}
              align={FLEX_ALIGN.LEFT}
              color={theme.colors.black}
            >
              <SafeFormattedMessageWithoutSpread
                message={messagesForm.orderReferenceHelperTitle}
              />
            </Heading>
          </$HelperContainerTitle>
          <SafeFormattedMessageWithoutSpread
            message={sellerConfiguration?.orderHelpText}
          />
          {sellerConfiguration?.orderHelpImageUrls.map((url) => {
            return (
              <FormGroup key={url}>
                <$Img src={url} alt="helperImage" />
              </FormGroup>
            );
          })}
        </SwipeableDrawer>
      )}
    </FormProvider>
  );
};

PurchaseDateLoginEnable.displayName = 'PurchaseDateLoginEnable';

export default PurchaseDateLoginEnable;
